<template>
    <div class="perm-list">
        <div v-if="accountRows.length" class="perm-cards">
            <section v-for="row in accountRows" :key="row.name" class="perm-card" :class="{ locked: !!locked[row.name] }">
                <div class="perm-card-copy">
                    <strong>{{ t(row.label) }}</strong>
                    <p v-if="row.help">{{ t(row.help) }}</p>
                    <p v-if="locked[row.name]" class="locked-note">{{ locked[row.name] }}</p>
                </div>
                <kv-switch
                    :id="'perm-' + row.name"
                    :value="flag(row.name, 'enabled')"
                    return-type="number"
                    :aria-disabled="!!locked[row.name]"
                    @input="setEnabled(row.name, $event)"
                />
            </section>
        </div>

        <h3 v-if="moduleRows.length" class="modules-title">{{ t('users-create-modules') }}</h3>
        <section v-for="row in moduleRows" :key="row.name" class="perm-block">
            <kv-switch
                :id="'perm-' + row.name"
                :value="flag(row.name, 'enabled')"
                return-type="number"
                :label="labelFor(row)"
                @input="setEnabled(row.name, $event)"
            />
            <p v-if="row.help" class="help">{{ t(row.help) }}</p>

            <div v-if="isOn(row.name) && row.level" class="sub">
                <label class="sub-label">
                    <span>{{ t(row.level.prompt) }}</span>
                    <select v-model="permissions[row.name].options.level" required>
                        <option disabled value="">{{ t(row.level.title) }}</option>
                        <option v-for="option in row.level.options" :key="option.value" :value="option.value">{{ t(option.label) }}</option>
                    </select>
                </label>
            </div>

            <div v-if="isOn(row.name) && row.toggles" class="sub toggles">
                <kv-switch
                    v-for="toggle in row.toggles"
                    :key="toggle.key"
                    :id="'perm-' + row.name + '-' + toggle.key"
                    :value="flag(row.name, toggle.key)"
                    return-type="number"
                    :label="t(toggle.label)"
                    @input="setOption(row.name, toggle.key, $event)"
                />
            </div>
        </section>
    </div>
</template>

<script>
import KvSwitch from "../components/inputs/kv-switch.vue";

const CATALOG = [
    {
        name: "dashboard",
        label: "places-user_form-permission-dashboard",
        help: "places-user_form-permission-dashboard_help",
        level: {
            prompt: "places-user_form-permission-access_operations",
            title: "places-user_form-permission-dashboard_access_title",
            options: [
                { value: "insert", label: "places-user_form-permission-level_insert" },
                { value: "read", label: "places-user_form-permission-level_read" },
            ],
        },
    },
    {
        name: "inbox",
        label: "places-user_form-permission-inbox",
        help: "places-user_form-permission-inbox_help",
        toggles: [{ key: "global_access", label: "places-user_form-permission-global_access" }],
    },
    {
        name: "tasks",
        label: "places-user_form-permission-tasks",
        help: "places-user_form-permission-tasks_help",
        level: {
            prompt: "places-user_form-permission-access_tasks",
            title: "places-user_form-permission-tasks_access_title",
            options: [
                { value: "mine", label: "places-user_form-permission-scope_mine" },
                { value: "branch", label: "places-user_form-permission-scope_branch" },
                { value: "all", label: "places-user_form-permission-scope_all_feminine" },
            ],
        },
    },
    {
        name: "coupons",
        label: "places-user_form-permission-coupons",
        help: "places-user_form-permission-coupons_help",
    },
    {
        name: "clients",
        label: "places-user_form-permission-clients",
        help: "places-user_form-permission-clients_help",
        toggles: [
            { key: "global_access", label: "places-user_form-permission-global_access" },
            { key: "deletion", label: "places-user_form-permission-delete_clients" },
        ],
    },
    {
        name: "segments",
        label: "places-user_form-permission-segments",
        help: "places-user_form-permission-segments_help",
    },
    {
        name: "rewards",
        label: "places-user_form-permission-rewards",
        help: "places-user_form-permission-rewards_help",
    },
    {
        name: "reports",
        label: "places-user_form-permission-reports",
        help: "places-user_form-permission-reports_help",
        toggles: [{ key: "global_access", label: "places-user_form-permission-global_access" }],
    },
    {
        name: "forms",
        label: "places-user_form-permission-forms",
        help: "places-user_form-permission-forms_help",
    },
    {
        name: "automations",
        label: "places-user_form-permission-automations",
        help: "places-user_form-permission-automations_help",
    },
    {
        name: "balance",
        label: "places-user_form-permission-balance",
        help: "places-user_form-permission-balance_help",
    },
    {
        name: "configs",
        label: "places-user_form-permission-configs",
        help: "places-user_form-permission-configs_help",
    },
    {
        name: "quotations",
        label: "places-user_form-permission-quotations",
        help: "places-user_form-permission-quotations_help",
        level: {
            prompt: "places-user_form-permission-access_quotations",
            title: "places-user_form-permission-quotations_access_title",
            options: [
                { value: "mine", label: "places-user_form-permission-scope_mine_plural" },
                { value: "branch", label: "places-user_form-permission-scope_branch" },
                { value: "all", label: "places-user_form-permission-scope_all_plural" },
            ],
        },
    },
    {
        name: "mcp",
        label: "places-user_form-permission-mcp",
        help: "places-user_form-permission-mcp_help",
    },
    {
        name: "invoices",
        label: "places-user_form-permission-invoices",
        help: "places-user_form-permission-invoices_help",
        toggles: [{ key: "global_access", label: "places-user_form-permission-global_access" }],
    },
    {
        name: "benefit_programs",
        label: "places-user_form-permission-benefit_programs",
        help: "places-user_form-permission-benefit_programs_help",
    },
    {
        name: "account.users.manage",
        label: "users-create-users",
        help: "users-create-users_help",
    },
];

const ACCOUNT_PERMISSIONS = ["account.users.manage"];

function defaultOptions(name) {
    const row = CATALOG.find((item) => item.name === name);
    const options = {};
    if (!row) return options;
    if (row.level) options.level = "";
    (row.toggles || []).forEach((toggle) => {
        options[toggle.key] = 0;
    });
    return options;
}

export default {
    name: "permissions-editor",
    components: { "kv-switch": KvSwitch },
    props: {
        permissions: { type: Object, required: true },
        // { "account.users.manage": "motivo" } trava o toggle e mostra o motivo.
        locked: { type: Object, default: () => ({}) },
    },
    computed: {
        visibleRows() {
            return CATALOG.filter((row) => this.permissions && this.permissions[row.name]);
        },
        accountRows() {
            return ACCOUNT_PERMISSIONS
                .map((name) => this.visibleRows.find((row) => row.name === name))
                .filter(Boolean);
        },
        moduleRows() {
            return this.visibleRows.filter((row) => !ACCOUNT_PERMISSIONS.includes(row.name));
        },
    },
    watch: {
        permissions: {
            immediate: true,
            handler(value) {
                if (!value) return;
                CATALOG.forEach((row) => this.ensure(value, row.name));
            },
        },
    },
    methods: {
        t(term, replaces) {
            return this.$root.t(term, replaces);
        },
        labelFor(row) {
            return this.t(row.label);
        },
        isOn(name) {
            const row = this.permissions[name];
            return !!(row && (row.enabled === true || row.enabled == 1));
        },
        flag(name, key) {
            const row = this.permissions[name];
            if (!row) return 0;
            if (key === "enabled") return row.enabled == 1 || row.enabled === true ? 1 : 0;
            const value = row.options && row.options[key];
            return value == 1 || value === true ? 1 : 0;
        },
        ensure(target, name) {
            const row = target[name];
            if (!row) return;
            if (!row.options || typeof row.options !== "object" || Array.isArray(row.options)) {
                this.$set(row, "options", {});
            }
            const defaults = defaultOptions(name);
            Object.keys(defaults).forEach((key) => {
                if (row.options[key] === undefined || row.options[key] === null) {
                    this.$set(row.options, key, defaults[key]);
                }
            });
        },
        setEnabled(name, value) {
            if (this.locked[name]) {
                // Reverte o switch visual: o valor salvo continua o mesmo.
                this.$forceUpdate();
                return;
            }
            this.ensure(this.permissions, name);
            this.$set(this.permissions[name], "enabled", value);
        },
        setOption(name, key, value) {
            this.ensure(this.permissions, name);
            this.$set(this.permissions[name].options, key, value);
        },
    },
};
</script>

<style scoped>
.perm-list { display: flex; flex-direction: column; }
.perm-cards { display: flex; flex-direction: column; gap: 8px; margin-bottom: 16px; }
.perm-card { display: flex; align-items: center; justify-content: space-between; gap: 16px; padding: 16px; border-radius: 12px; background: #f9f9fb; border: 1px solid #f3f3f6; }
.perm-card-copy { display: flex; flex-direction: column; gap: 4px; min-width: 0; }
.perm-card-copy strong { font-family: "Sora", sans-serif; font-size: 14px; font-weight: 500; line-height: 1.3; color: #383c50; }
.perm-card-copy p { margin: 0; font-family: "Inter", sans-serif; font-size: 12px; line-height: 1.3; color: #626473; }
.perm-card .kv-switch-wrapper { flex: none; }
.perm-card.locked .kv-switch-wrapper { opacity: .5; pointer-events: none; }
.locked-note { color: #9d530f !important; }
.modules-title { margin: 8px 0 12px; font-family: "Sora", sans-serif; font-size: 14px; font-weight: 500; color: #383c50; }
.perm-block { padding: 14px 0; border-bottom: 1px solid #e5e6eb; }
.perm-block:first-child { padding-top: 0; }
.help { margin: 4px 0 0 60px; color: #626473; font-family: "Inter", sans-serif; font-size: 12px; line-height: 1.4; }
.sub { margin: 12px 0 0 60px; display: flex; flex-direction: column; gap: 10px; }
.sub-label { display: flex; flex-direction: column; gap: 6px; font-family: "Inter", sans-serif; font-size: 13px; color: #383c50; }
.sub-label select { height: 40px; border-radius: 8px; border: 1px solid #e5e6eb; background: #f9f9fb; padding: 0 12px; color: #383c50; font-size: 13px; }
.toggles { gap: 8px; }
</style>
